import imageCompression from "browser-image-compression";
import { useEffect, useState } from "react";
import { FaChevronLeft, FaChevronRight, FaTrashAlt } from "react-icons/fa";
import { GetSession } from "../../../utils/CheckSession";

// PhotoManager – správa fotek existujícího stroje: mazání jednotlivých fotek,
// změna pořadí (drag & drop) a přidávání nových. Každá akce se ukládá okamžitě.
// První fotka v pořadí (position 0) je náhledová.
export const PhotoManager = ({ machineId }) => {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [dragIndex, setDragIndex] = useState(null);
  const [overIndex, setOverIndex] = useState(null);

  const authHeaders = { Authorization: "token " + GetSession() };

  const loadPhotos = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_URL}/machine/admin/get-photos`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders },
        body: JSON.stringify({ machineId: String(machineId) }),
      });
      if (res.status === 200) {
        setPhotos(await res.json());
      } else {
        console.error("get-photos status", res.status);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (machineId) loadPhotos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [machineId]);

  const handleDelete = async (photo) => {
    if (!window.confirm("Opravdu smazat tuto fotku? Akce je nevratná.")) return;
    setBusy(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_URL}/machine/admin/delete-image`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders },
        body: JSON.stringify({ photoId: String(photo.id) }),
      });
      if (res.status === 200) {
        setPhotos(await res.json());
      } else {
        alert("Fotku se nepodařilo smazat");
      }
    } catch (e) {
      console.error(e);
      alert(e);
    } finally {
      setBusy(false);
    }
  };

  const saveOrder = async (ordered) => {
    setBusy(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_URL}/machine/admin/reorder-images`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders },
        body: JSON.stringify({ machineId: String(machineId), order: ordered.map((p) => p.id) }),
      });
      if (res.status === 200) {
        setPhotos(await res.json());
      } else {
        alert("Pořadí se nepodařilo uložit");
        loadPhotos();
      }
    } catch (e) {
      console.error(e);
      loadPhotos();
    } finally {
      setBusy(false);
    }
  };

  const onDrop = (dropIndex) => {
    setOverIndex(null);
    if (dragIndex === null || dragIndex === dropIndex) {
      setDragIndex(null);
      return;
    }
    const next = [...photos];
    const [moved] = next.splice(dragIndex, 1);
    next.splice(dropIndex, 0, moved);
    setDragIndex(null);
    setPhotos(next); // optimistické překreslení, backend potvrdí
    saveOrder(next);
  };

  // Posun fotky o jednu pozici (funguje i na dotykových zařízeních, kde drag & drop nefunguje).
  const move = (index, dir) => {
    const target = index + dir;
    if (target < 0 || target >= photos.length) return;
    const next = [...photos];
    [next[index], next[target]] = [next[target], next[index]];
    setPhotos(next); // optimisticky
    saveOrder(next);
  };

  const handleAdd = async (event) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;
    setBusy(true);
    try {
      const body = new FormData();
      body.append("machineId", String(machineId));
      for (let i = 0; i < files.length; i++) {
        const compressed = await imageCompression(files[i], { maxSizeMB: 0.2, maxWidthOrHeight: 1920, useWebWorker: true });
        body.append("photos", compressed, `photo-${i}.jpg`);
      }
      const res = await fetch(`${process.env.REACT_APP_URL}/machine/admin/add-image`, {
        method: "POST",
        headers: { ...authHeaders },
        body,
      });
      if (res.status === 200) {
        setPhotos(await res.json());
      } else {
        alert("Fotky se nepodařilo přidat");
      }
    } catch (e) {
      console.error(e);
      alert(e);
    } finally {
      setBusy(false);
      event.target.value = ""; // umožní znovu vybrat stejný soubor
    }
  };

  if (!machineId) return null;

  return (
    <div style={{ marginTop: "1.5rem" }}>
      <h1 style={{ marginBottom: "0.25rem" }}>Fotky stroje</h1>
      <p style={{ color: "#666", fontSize: "13px", marginTop: 0 }}>
        Přetažením změníte pořadí, ikonou koše smažete jednotlivou fotku. První fotka je náhledová. Změny se ukládají okamžitě.
      </p>

      {loading ? (
        <p>Načítám fotky…</p>
      ) : photos.length === 0 ? (
        <p style={{ color: "#999" }}>Zatím žádné fotky.</p>
      ) : (
        <div style={{ display: "flex", flexWrap: "wrap", gap: "12px", opacity: busy ? 0.6 : 1, pointerEvents: busy ? "none" : "auto" }}>
          {photos.map((photo, index) => (
            <div
              key={photo.id}
              draggable
              onDragStart={() => setDragIndex(index)}
              onDragOver={(e) => {
                e.preventDefault();
                if (overIndex !== index) setOverIndex(index);
              }}
              onDrop={() => onDrop(index)}
              onDragEnd={() => {
                setDragIndex(null);
                setOverIndex(null);
              }}
              style={{
                position: "relative",
                width: "140px",
                height: "140px",
                borderRadius: "8px",
                overflow: "hidden",
                cursor: "grab",
                border: overIndex === index ? "2px solid #E00B0B" : "2px solid transparent",
                boxShadow: "0 1px 4px rgba(0,0,0,0.2)",
                background: `#f2f2f2 url(${photo.url}) center/cover no-repeat`,
              }}
              title="Přetáhněte pro změnu pořadí"
            >
              {index === 0 && (
                <span style={{ position: "absolute", top: 6, left: 6, background: "#262626", color: "#fff", fontSize: "11px", padding: "2px 6px", borderRadius: "4px" }}>Náhled</span>
              )}
              <button
                type="button"
                onClick={() => handleDelete(photo)}
                style={{
                  position: "absolute",
                  top: 6,
                  right: 6,
                  width: "28px",
                  height: "28px",
                  borderRadius: "50%",
                  border: "none",
                  background: "rgba(224,11,11,0.9)",
                  color: "#fff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                }}
                title="Smazat fotku"
              >
                <FaTrashAlt size={13} />
              </button>

              {/* Šipky pro změnu pořadí – fungují i na dotyku (drag & drop na desktopu) */}
              <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, display: "flex", justifyContent: "space-between", background: "rgba(0,0,0,0.45)" }}>
                <button
                  type="button"
                  onClick={() => move(index, -1)}
                  disabled={index === 0}
                  style={{
                    flex: 1,
                    border: "none",
                    background: "transparent",
                    color: index === 0 ? "rgba(255,255,255,0.3)" : "#fff",
                    padding: "7px 0",
                    cursor: index === 0 ? "default" : "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                  title="Posunout doleva"
                >
                  <FaChevronLeft size={12} />
                </button>
                <button
                  type="button"
                  onClick={() => move(index, 1)}
                  disabled={index === photos.length - 1}
                  style={{
                    flex: 1,
                    border: "none",
                    background: "transparent",
                    color: index === photos.length - 1 ? "rgba(255,255,255,0.3)" : "#fff",
                    padding: "7px 0",
                    cursor: index === photos.length - 1 ? "default" : "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                  title="Posunout doprava"
                >
                  <FaChevronRight size={12} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <div style={{ marginTop: "1rem" }}>
        <label style={{ display: "inline-block", background: "#262626", color: "#fff", padding: "10px 16px", borderRadius: "6px", cursor: busy ? "not-allowed" : "pointer" }}>
          + Přidat fotky
          <input type="file" accept="image/jpeg" multiple disabled={busy} onChange={handleAdd} style={{ display: "none" }} />
        </label>
      </div>
    </div>
  );
};

export default PhotoManager;
